// src/messaging/ConversationPanels.jsx
// 会話の「⋯」から開く画面: 検索・メンバー・グループ名の変更【2026-10-16】
import React, { useState } from 'react';
import { msgApi } from './api';
import { dateTime } from './util';
import { Modal } from './ui';
import { confirmDialog } from '../components/ConfirmDialog';

// ── 検索 ──────────────────────────────────────────────
export function SearchPanel({ conversationId, onClose, onJump }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState('');
  const run = async (e) => {
    e && e.preventDefault();
    if (!q.trim() || busy) return;
    setBusy(true); setErr('');
    const r = await msgApi.get(`/conversations/${conversationId}/search?q=${encodeURIComponent(q.trim())}`);
    setBusy(false);
    if (!r.success) { setErr(r.message || '検索できませんでした'); return; }
    setRes(r);
  };
  return (
    <Modal title="この会話を検索" onClose={onClose}>
      <form className="msg-inline-form" onSubmit={run}>
        <input type="search" className="msg-field" value={q} maxLength={100} autoFocus
               onChange={e => setQ(e.target.value)} placeholder="探す言葉" aria-label="探す言葉" />
        <button type="submit" className="btn-primary" disabled={busy || !q.trim()}>{busy ? '検索中…' : '検索'}</button>
      </form>
      {err && <p className="msg-error">{err}</p>}
      {res && (
        <>
          {res.results.length === 0 && <p className="msg-help">見つかりませんでした。</p>}
          <ul className="msg-search-results">
            {res.results.map(h => (
              <li key={h.id}>
                <button type="button" onClick={() => { onJump(h.id); onClose(); }}>
                  <span className="msg-search-meta">{h.sender ? h.sender.name : ''}　{dateTime(h.created_at)}</span>
                  <span className="msg-search-snippet">{h.snippet}</span>
                </button>
              </li>
            ))}
          </ul>
          {!res.complete && <p className="msg-help">新しい方から {res.scanned} 件の中を探しました。それより古いメッセージは探していません。</p>}
        </>
      )}
    </Modal>
  );
}

// ── グループ名の変更 ─────────────────────────────────────
export function RenamePanel({ conversationId, current, onClose, onDone }) {
  const [title, setTitle] = useState(current || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const save = async (e) => {
    e && e.preventDefault();
    if (!title.trim()) { setErr('グループ名を入れてください'); return; }
    setBusy(true); setErr('');
    const r = await msgApi.patch(`/conversations/${conversationId}`, { title: title.trim() });
    setBusy(false);
    if (!r.success) { setErr(r.message || '変えられませんでした'); return; }
    onDone(); onClose();
  };
  return (
    <Modal title="グループ名の変更" onClose={onClose}
           footer={<>
             <button type="button" className="btn-secondary" onClick={onClose}>やめる</button>
             <button type="button" className="btn-primary" onClick={save} disabled={busy}>変更する</button>
           </>}>
      <form onSubmit={save} className="form-row">
        <label htmlFor="msg-rename">グループ名（100文字まで）</label>
        <input id="msg-rename" type="text" value={title} maxLength={100} onChange={e => setTitle(e.target.value)} autoFocus />
      </form>
      <p className="msg-help">変えると、メンバー全員の画面に「グループ名を変えました」と出ます。</p>
      {err && <p className="msg-error">{err}</p>}
    </Modal>
  );
}

// ── メンバー ───────────────────────────────────────────
const ROLE = { owner: '作った人', teacher: '先生', student: '学生' };

function DirectRequestForm({ conversationId, member, onDone, onOpenConversation }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const send = async () => {
    setBusy(true); setMsg('');
    const r = await msgApi.post('/requests/group-member', {
      user_id: member.id, conversation_id: conversationId, message: note.trim() || undefined,
    });
    setBusy(false);
    if (!r.success) { setMsg(r.message || '送れませんでした'); return; }
    if (r.already && r.conversation_id) { onOpenConversation(r.conversation_id); return; }
    setMsg(r.message || '申請しました。相手が承認すると始められます');
    setNote('');
    onDone && onDone();
  };
  return (
    <div className="msg-subform">
      <p className="msg-help">{member.name}さんが承認すると、1対1のメッセージを始められます。</p>
      <textarea rows={2} className="msg-field" maxLength={200} value={note} onChange={e => setNote(e.target.value)}
                placeholder="ひとこと（なくてもかまいません・200文字まで）" aria-label="ひとこと" />
      <div className="msg-subform-foot">
        <span className="msg-count">{note.length}/200</span>
        <button type="button" className="btn-primary" onClick={send} disabled={busy}>申請する</button>
      </div>
      {msg && <p className="msg-help strong">{msg}</p>}
    </div>
  );
}

function InviteByIdForm({ conversationId }) {
  const [sid, setSid] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const send = async (e) => {
    e && e.preventDefault();
    if (!sid.trim()) return;
    setBusy(true); setMsg('');
    const r = await msgApi.post('/requests/student-id', {
      student_id: sid.trim(), conversation_id: conversationId, message: note.trim() || undefined,
    });
    setBusy(false);
    if (!r.success) { setMsg(r.message || '送れませんでした'); return; }
    setMsg('リクエストを送りました');
    setSid(''); setNote('');
  };
  return (
    <form className="msg-subform" onSubmit={send}>
      <h4>学籍番号でこのグループに誘う</h4>
      <input type="text" className="msg-field" value={sid} onChange={e => setSid(e.target.value)} placeholder="学籍番号" aria-label="学籍番号" />
      <textarea rows={2} className="msg-field" maxLength={200} value={note} onChange={e => setNote(e.target.value)}
                placeholder="ひとこと（なくてもかまいません・200文字まで）" aria-label="ひとこと" />
      <div className="msg-subform-foot">
        <span className="msg-count">{note.length}/200</span>
        <button type="submit" className="btn-primary" disabled={busy || !sid.trim()}>誘う</button>
      </div>
      {msg && <p className="msg-help strong">{msg}</p>}
    </form>
  );
}

export function MembersPanel({ detail, meId, isStudent, studentChat, writable, onClose, onChanged, onOpenConversation }) {
  const [openFor, setOpenFor] = useState(null);
  const [err, setErr] = useState('');
  const members = (detail.members || []).filter(m => !m.left);
  const isOwner = detail.kind === 'group' && detail.my_role === 'owner';
  const canDirect = isStudent && studentChat && writable && (detail.kind === 'group' || detail.kind === 'class_group');

  const remove = async (m) => {
    if (!(await confirmDialog(`${m.name}さんをグループから外しますか？`))) return;
    setErr('');
    const r = await msgApi.del(`/conversations/${detail.id}/members/${m.id}`);
    if (!r.success) { setErr(r.message || '外せませんでした'); return; }
    onChanged();
  };

  return (
    <Modal title={`メンバー（${members.length}人）`} onClose={onClose}>
      {err && <p className="msg-error">{err}</p>}
      {detail.left && <p className="msg-help">この会話から抜けているため、今のメンバーは表示されません。</p>}
      <ul className="msg-member-list">
        {members.map(m => (
          <li key={m.id}>
            <div className="msg-member-row">
              <span className="msg-member-name">{m.name}{m.id === meId && <span className="msg-tag">あなた</span>}</span>
              {ROLE[m.role] && m.role !== 'member' && m.role !== 'student' && <span className="msg-tag">{ROLE[m.role]}</span>}
              <span className="msg-member-actions">
                {canDirect && m.id !== meId && (
                  <button type="button" className="btn-link" onClick={() => setOpenFor(openFor === m.id ? null : m.id)}>個別にメッセージ</button>
                )}
                {isOwner && writable && m.id !== meId && (
                  <button type="button" className="btn-link danger" onClick={() => remove(m)}>外す</button>
                )}
              </span>
            </div>
            {openFor === m.id && (
              <DirectRequestForm conversationId={detail.id} member={m} onOpenConversation={(id) => { onClose(); onOpenConversation(id); }} />
            )}
          </li>
        ))}
      </ul>
      {detail.kind === 'group' && isStudent && studentChat && writable && <InviteByIdForm conversationId={detail.id} />}
      {detail.kind === 'group' && isStudent && <p className="msg-help">QR コード・近づけて追加はスマホのアプリで使えます。</p>}
    </Modal>
  );
}
